/**
 * BehaviorTracker - system śledzenia danych behawioralnych użytkownika na wniosku pożyczkowym
 * 
 * Zbiera sygnały mogące pomóc w ocenie ryzyka i wykryciu fraudów:
 * - timing wypełniania pól (zbyt szybkie = autofill/bot, zbyt wolne = przepisywanie z cudzego dokumentu)
 * - wzorce wpisywania (kopiuj-wklej vs ręczne wpisywanie)
 * - poprawki i cofanie danych (wielokrotne zmiany = niepewność/testowanie)
 * - nawigacja po formularzu (sekwencja kroków, cofanie)
 * - interakcja z kalkulatorem (ile razy zmieniano kwotę)
 * - dane urządzeniowe i środowiskowe
 * - focus/blur okna (przełączanie do innej zakładki = czytanie cudzych danych?)
 * - zachowanie myszy i dotykowe
 * - detekcja narzędzi deweloperskich
 * - keystroke dynamics: dwell (czas trzymania klawisza) + flight (przerwa między klawiszami) [v1.5.0]
 * - isTrusted na zdarzeniach: syntetyczne keydown/paste = automation/bot [v1.5.0]
 * - sensor mobilny (accel/orientacja): emulator/spoof detection [v1.5.0]
 * - surowa sekwencja zdarzeń: do weryfikacji integralności na backendzie [v1.5.0]
 * - honeypot: niewidzialne pole-przynęta, wypełnienie = bot [v1.6.0]
 * 
 * Użycie:
 *   import BehaviorTracker from '@/behaviorTracker';
 *   
 *   // w komponencie Vue (mixin automatycznie inicjalizuje)
 *   // lub ręcznie:
 *   BehaviorTracker.init();
 */

var BehaviorTracker = (function () {

  'use strict';

  // ============================================
  // STAN WEWNĘTRZNY
  // ============================================

  var _initialized = false;
  var _formStartTime = null;
  var _stepStartTime = null;
  var _currentStep = 1;

  // timing per-field
  var _fieldFocusTime = {};    // kiedy user wszedł w pole
  var _fieldDurations = {};    // łączny czas spędzony w polu (ms)
  var _fieldKeystrokeTimes = {}; // tablica timestamps klawiszy per pole
  var _fieldChangeCount = {};  // ile razy zmieniano wartość pola
  var _fieldPasteCount = {};   // ile razy wklejano do pola
  var _fieldValues = {};       // historia wartości pola [{ value, timestamp }]
  var _fieldFocusCount = {};   // ile razy focusowano pole

  // globalne metryki
  var _totalKeystrokes = 0;
  var _totalClicks = 0;
  var _totalPastes = 0;
  var _totalCopies = 0;
  var _tabSwitchCount = 0;
  var _tabAwayDuration = 0;
  var _tabHiddenSince = null;
  var _mouseDistance = 0;
  var _lastMousePos = null;
  var _scrollCount = 0;
  var _hesitations = [];        // momenty gdy user nic nie robił > 5s w polu
  var _rapidFields = [];        // pola wypełnione < 300ms (autofill/bot)
  var _correctedFields = {};    // pola gdzie user cofał/zmieniał dane
  var _backspaceCount = 0;

  // nawigacja
  var _stepTimeline = [];       // [{ step, enterTime, leaveTime, duration }]
  var _backStepCount = 0;

  // kalkulator
  var _calcChangeCount = 0;
  var _calcValues = [];

  // touch/mobile
  var _touchCount = 0;
  var _isTouchDevice = false;

  // devtools
  var _devToolsOpened = false;
  var _windowResizeCount = 0;

  // submission
  var _submitAttempts = 0;
  var _validationErrors = [];

  // kolejność pól (w jakiej sekwencji user wypełniał)
  var _fieldOrder = [];          // [{ field, ts }]

  // autofill detection - pola zmienione bez keystroke (browser autofill)
  var _autofillSuspects = {};    // field => { valueLen, hadKeystrokes }

  // right-click (podejrzane w formularzach)
  var _rightClickCount = 0;
  var _rightClickFields = [];

  // mouse linearity - proste linie myszy = bot
  var _mouseSegments = [];       // mini segmenty do analizy krzywizny
  var _mouseMoveThrottle = 0;

  // field value lengths at submit (do korelacji z danymi backendu)
  var _fieldLengthsAtBlur = {};

  // tab switches z kontekstem - które pole było aktywne
  var _tabSwitchContext = [];    // [{ field, ts, awayDuration }]

  // czas pierwszego i ostatniego wklejenia (paste timing)
  var _firstPasteTime = null;
  var _lastPasteTime = null;

  // select/dropdown changes
  var _selectChanges = {};       // field => [{ value, ts }]

  // === v1.5.0: isTrusted / detekcja syntetycznych zdarzeń ===
  // isTrusted===false => event wygenerowany skryptem (automation/bot), nie userem.
  // Prawdziwe pisanie/wklejanie ma zawsze isTrusted===true.
  var _untrustedEvents = 0;      // łączna liczba eventów z isTrusted===false
  var _trustedEvents = 0;        // referencja (isTrusted===true)
  var _untrustedByType = {};     // typ eventu => liczba untrusted (keydown/paste/click)

  // === v1.5.0: keystroke dynamics (dwell + flight) ===
  // dwell  = czas trzymania klawisza (keydown→keyup tego samego klawisza)
  // flight = przerwa między klawiszami (keyup→następny keydown)
  // Bogatszy i trudniejszy do podrobienia niż same interwały keydown.
  var _keyDownAt = {};           // field => { code => ts } pairing keydown↔keyup
  var _fieldDwell = {};          // field => [dwell ms]
  var _fieldFlight = {};         // field => [flight ms]
  var _lastKeyUpAt = {};         // field => ts ostatniego keyup (do flight)

  // === v1.5.0: sensor mobilny (emulator/spoof detection) ===
  var _motionSamples = [];       // accelerationIncludingGravity {x,y,z}
  var _orientSamples = [];       // {a,b,g}
  var _motionEventCount = 0;
  var _orientEventCount = 0;
  var _SENSOR_MAX = 200;         // cap próbek (jak Nethone)

  // === v1.5.0: surowa sekwencja zdarzeń (tamper-detection na backendzie) ===
  // bounded ring buffer; tylko META (typ, pole, isTrusted, czas) — ZERO wartości pól.
  // monotoniczny licznik pozwala backendowi wykryć braki/manipulację.
  var _eventSeq = [];            // [{ n, t, e, f, tr }]
  var _eventSeqCounter = 0;
  var _EVENTSEQ_MAX = 150;

  // === v1.6.0: honeypot ===
  // Niewidzialne pole-przynęta: człowiek go nie widzi i nie sfokusuje (offscreen,
  // tabindex=-1), bot wypełniający wszystkie inputy — tak. Nazwa udaje realne
  // pole ('client_fax'), autocomplete=off chroni przed autofillem przeglądarki.
  // Jak _devToolsOpened: raz wypełnione = flaga do końca sesji (NIE resetowana).
  var _honeypotEl = null;
  var _honeypotWrap = null;
  var _honeypotFilled = false;
  var _honeypotMeta = { len: 0, inputs: 0, focused: 0, firstTs: null };
  var _HONEYPOT_NAME = 'client_fax';

  // auth & prefill context
  var _isAuthenticated = false;  // user jest zalogowany
  var _formPrefilled = false;    // pola wypełnione programowo (fillForm)
  var _prefillTime = null;       // kiedy nastąpił prefill
  var _prefilledFields = [];     // które pola były prefillowane
  var _applicationPhase = 'new'; // 'new' | 'continuation' | 'resubmit_after_verification'
  var _hasLoanId = false;        // wniosek ma już loan_id (kontynuacja)

  // dodatkowe
  var _focusedField = null;
  var _lastActivityTime = null;
  var _idlePeriods = [];        // okresy bezczynności > 30s
  var _idleStart = null;
  var _IDLE_THRESHOLD = 30000;
  var _HESITATION_THRESHOLD = 5000;

  // one-shot flag dla excessive_calc_changes (anty-spam w logach)
  var _calcExcessiveLogged = false;

  // pola maskowane v-mask — generują syntetyczne backspace przy reformatowaniu,
  // więc rytm i excessive_corrections są tu nierzetelne
  var _MASK_FIELDS_RX = /^(client_bank_account|client_PESEL|client_cellphone|client_identity_card|client_name|client_surname|client_address1_postal_code|Kod\s*SMS|Kod)$/;

  // pola gdzie paste/right-click jest w 100% legitne (kopiuj z dokumentu/maila/SMS)
  var _LEGIT_PASTE_RX = /^(client_bank_account|email_confirm|client_email|Kod\s*SMS|Kod)$/;

  // pola gdzie tab-switch jest oczekiwany (otwarcie password managera, czytanie SMS, sprawdzenie maila)
  var _LEGIT_TABSWITCH_RX = /^(_login_|Kod|email_confirm)/;


  // ============================================
  // NARZĘDZIA WEWNĘTRZNE
  // ============================================

  function _now() {
    return Date.now();
  }

  // input.type który ma sens dla detekcji autofill / rapid fill (text-like)
  // Wykluczamy checkbox/radio/select/file/itd. — kliknięcie jest natychmiastowe i bez keystroke,
  // więc heurystyka "no keystrokes + has value" generowała masę false positives.
  function _isTextInput(el) {
    if (!el || !el.tagName) return false;
    var tag = el.tagName.toUpperCase();
    if (tag === 'TEXTAREA') return true;
    if (tag !== 'INPUT') return false;
    var type = (el.type || 'text').toLowerCase();
    return /^(text|email|tel|password|number|search|url|date|month|week|time|datetime-local)$/.test(type);
  }

  function _isMaskField(name) {
    return _MASK_FIELDS_RX.test(name || '');
  }

  function _isLegitPasteField(name) {
    return _LEGIT_PASTE_RX.test(name || '');
  }

  function _isLegitTabField(name) {
    return name && _LEGIT_TABSWITCH_RX.test(name);
  }

  function _isLoginField(name) {
    return /^_login_/.test(name || '');
  }

  // Cichy: nic nie pisze do console (LogService buforuje i POST-uje do /log).
  // Prefix skrócony do 'BT' — minimalny marker w ewentualnym wycieku, bez ujawniania celu.
  function _log() {
    if (window.LogService) {
      var args = ['BT'].concat(Array.prototype.slice.call(arguments));
      window.LogService.log.apply(window.LogService, args);
    }
  }

  function _getFieldName(el) {
    // direct attributes
    var name = el.getAttribute('data-field')
      || el.getAttribute('name')
      || el.getAttribute('id')
      || el.getAttribute('placeholder');

    // jeśli brak — szukaj data-field na rodzicu (Vue wrapper)
    if (!name) {
      var parent = el.parentElement;
      for (var i = 0; i < 4 && parent; i++) {
        var pf = parent.getAttribute && parent.getAttribute('data-field');
        if (pf) { name = pf; break; }
        parent = parent.parentElement;
      }
    }

    if (!name) {
      name = 'unknown_' + el.tagName;
    }

    // taguj pola logowania — password manager wypełnia je natychmiastowo,
    // nie chcemy tego w risk signals formularza
    if (el.type === 'password' || /^(Login|Hasło|login|password)$/i.test(name)) {
      name = '_login_' + name;
    }

    return name;
  }

  function _isFormField(el) {
    if (!el || !el.tagName) return false;
    var tag = el.tagName.toUpperCase();
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
  }

  function _detectEnvironment() {
    var nav = navigator || {};
    var scr = screen || {};

    var env = {
      // rozmiar okna vs ekran (podejrzane jeśli mocno różne - headless?)
      screenSize: scr.width + 'x' + scr.height,
      windowSize: window.innerWidth + 'x' + window.innerHeight,
      devicePixelRatio: window.devicePixelRatio || 1,
      colorDepth: scr.colorDepth || 0,

      // timezone
      timezone: (function () {
        try { return Intl.DateTimeFormat().resolvedOptions().timeZone; }
        catch (e) { return 'unknown'; }
      })(),
      timezoneOffset: new Date().getTimezoneOffset(),

      // język
      language: nav.language || nav.userLanguage || 'unknown',
      languages: (nav.languages || []).join(','),

      // platforma
      platform: nav.platform || 'unknown',
      hardwareConcurrency: nav.hardwareConcurrency || 0,
      deviceMemory: nav.deviceMemory || 0,
      maxTouchPoints: nav.maxTouchPoints || 0,

      // połączenie
      connectionType: (nav.connection && nav.connection.effectiveType) || 'unknown',
      connectionDownlink: (nav.connection && nav.connection.downlink) || 0,

      // podejrzane flagi
      webdriver: !!nav.webdriver,
      headless: /headless/i.test(nav.userAgent),
      phantomJS: !!window._phantom || !!window.callPhantom,
      selenium: !!window.__selenium_unwrapped || !!document.__selenium_unwrapped,
      automationControlled: !!nav.webdriver,

      // dodatkowe bot detection
      // Puppeteer / Chrome DevTools Protocol  
      chromeRuntime: !!(window.chrome && (window.chrome.runtime || window.chrome.loadTimes)),
      hasNotificationPerm: (function () {
        try { return Notification.permission; } catch (e) { return 'error'; }
      })(),
      pluginCount: (nav.plugins || []).length,
      // brak pluginów na desktopie = podejrzane
      noPluginsOnDesktop: (nav.plugins || []).length === 0 && !/Mobi|Android|iPhone|iPad/i.test(nav.userAgent),
      // window.outerHeight === 0 = headless
      zeroOuterDimensions: window.outerHeight === 0 || window.outerWidth === 0,

      // Permissions API - boty zwykle nie mają nadanych
      // (async, zbieramy osobno)

      // referrer
      referrer: document.referrer || 'direct',

      // cookies enabled
      cookiesEnabled: nav.cookieEnabled,

      // doNotTrack
      doNotTrack: nav.doNotTrack || 'unknown',

      // performance - ile czasu zajął load strony (szybki = bot z cache/local)
      pageLoadTime: (function () {
        try {
          var perf = performance.getEntriesByType('navigation');
          if (perf && perf[0]) return Math.round(perf[0].loadEventEnd - perf[0].startTime);
          return 0;
        } catch (e) { return -1; }
      })(),

      // czy strona jest w iframe (phishing/embedding?)
      isIframe: window.self !== window.top,

      // storage availability
      hasLocalStorage: (function () {
        try { localStorage.setItem('_bt_test', '1'); localStorage.removeItem('_bt_test'); return true; }
        catch (e) { return false; }
      })(),
      hasSessionStorage: (function () {
        try { sessionStorage.setItem('_bt_test', '1'); sessionStorage.removeItem('_bt_test'); return true; }
        catch (e) { return false; }
      })()
    };

    // battery API (async) - niski poziom na desktopie = VM/bot
    if (nav.getBattery) {
      nav.getBattery().then(function (battery) {
        env.batteryCharging = battery.charging;
        env.batteryLevel = battery.level;
        // VM często raportuje 100% charging
        if (battery.charging && battery.level === 1 && !/Mobi|Android/i.test(nav.userAgent)) {
          _log('WARNING_possible_vm', 'battery always full+charging on desktop');
        }
      }).catch(function () { });
    }

    return env;
  }

  function _computeKeystrokeRhythm(fieldName) {
    var times = _fieldKeystrokeTimes[fieldName];
    if (!times || times.length < 3) return null;

    var intervals = [];
    for (var i = 1; i < times.length; i++) {
      intervals.push(times[i] - times[i - 1]);
    }

    var sum = 0, min = Infinity, max = 0;
    for (var j = 0; j < intervals.length; j++) {
      sum += intervals[j];
      if (intervals[j] < min) min = intervals[j];
      if (intervals[j] > max) max = intervals[j];
    }

    var avg = sum / intervals.length;

    // wariancja
    var variance = 0;
    for (var k = 0; k < intervals.length; k++) {
      variance += Math.pow(intervals[k] - avg, 2);
    }
    variance = variance / intervals.length;

    var stdDev = Math.sqrt(variance);

    // Klasyfikacja rytmu — była "stdDev<15 = bot", ale flagowała:
    //  - password manager (avg 1-3ms) — wkleja znak-po-znaku natychmiastowo
    //  - v-mask plugin (avg 50-60ms, bardzo regularnie) na IBAN/PESEL/cellphone
    //
    //  paste: avg < 10ms — credential manager / autofill char-by-char
    //  mask:  pole maskowane v-mask — syntetyczne keystrokes z reformatowania
    //  bot:   avg 20-200ms, std<5, count>20 — regularny bot wpisujący dane
    //  human: wszystko inne
    var rhythmClass = 'human';
    if (avg < 10) {
      rhythmClass = 'paste';
    } else if (_isMaskField(fieldName)) {
      rhythmClass = 'mask';
    } else if (stdDev < 5 && intervals.length > 20 && avg >= 20 && avg <= 200) {
      rhythmClass = 'bot';
    }

    return {
      avgInterval: Math.round(avg),
      minInterval: min,
      maxInterval: max,
      stdDev: Math.round(stdDev),
      count: intervals.length,
      rhythmClass: rhythmClass,
      // backward-compat: dawne flagowanie zostawiamy jako alias na 'bot'
      suspiciouslyRegular: rhythmClass === 'bot'
    };
  }


  // statystyki próbki (min/max/median/mean/sd) — format zgodny z Nethone
  function _stats(arr) {
    if (!arr || !arr.length) return null;
    var sorted = arr.slice().sort(function (a, b) { return a - b; });
    var n = sorted.length;
    var sum = 0, min = Infinity, max = -Infinity;
    for (var i = 0; i < n; i++) {
      sum += sorted[i];
      if (sorted[i] < min) min = sorted[i];
      if (sorted[i] > max) max = sorted[i];
    }
    var mean = sum / n;
    var variance = 0;
    for (var j = 0; j < n; j++) variance += Math.pow(sorted[j] - mean, 2);
    variance = variance / n;
    var mid = Math.floor(n / 2);
    var median = (n % 2 === 0) ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
    var r2 = function (x) { return Math.round(x * 100) / 100; };
    return {
      count: n, min: r2(min), max: r2(max),
      median: r2(median), mean: r2(mean), sd: r2(Math.sqrt(variance))
    };
  }

  // wspólne śledzenie isTrusted dla dowolnego eventu
  function _trackTrust(e, type) {
    if (e && e.isTrusted === false) {
      _untrustedEvents++;
      _untrustedByType[type] = (_untrustedByType[type] || 0) + 1;
    } else {
      _trustedEvents++;
    }
  }

  // zapis do bounded ring buffer surowej sekwencji zdarzeń (tylko meta, bez wartości)
  function _recordSeq(type, fieldName, isTrusted) {
    _eventSeqCounter++;
    _eventSeq.push({
      n: _eventSeqCounter,
      t: _now() - _formStartTime,
      e: type,
      f: fieldName || null,
      tr: isTrusted === false ? 0 : 1
    });
    if (_eventSeq.length > _EVENTSEQ_MAX) _eventSeq.shift();
  }

  // analiza sensora mobilnego — emulator/spoof detection
  function _analyzeSensor() {
    var ua = navigator.userAgent || '';
    if (!_isTouchDevice && !/Mobi|Android|iPhone|iPad/i.test(ua)) return null;

    function axis(samples, key) {
      var arr = [];
      for (var i = 0; i < samples.length; i++) arr.push(samples[i][key]);
      return _stats(arr);
    }

    // unchanging streak: najdłuższa seria identycznych próbek z rzędu.
    // Prawdziwy sensor (nawet leżący telefon) ma szum → krótkie streaki.
    // Emulator/spoof zwraca stałe wartości → długie streaki.
    function longestStreak(samples) {
      var longest = 0, cur = 0, prev = null;
      for (var i = 0; i < samples.length; i++) {
        var s = samples[i].x !== undefined
          ? (samples[i].x + ',' + samples[i].y + ',' + samples[i].z)
          : (samples[i].a + ',' + samples[i].b + ',' + samples[i].g);
        if (s === prev) { cur++; if (cur > longest) longest = cur; } else { cur = 0; }
        prev = s;
      }
      return longest;
    }

    return {
      motionEvents: _motionEventCount,
      orientEvents: _orientEventCount,
      motion: _motionSamples.length ? {
        x: axis(_motionSamples, 'x'),
        y: axis(_motionSamples, 'y'),
        z: axis(_motionSamples, 'z'),
        unchangingStreak: longestStreak(_motionSamples)
      } : null,
      orientation: _orientSamples.length ? {
        a: axis(_orientSamples, 'a'),
        b: axis(_orientSamples, 'b'),
        g: axis(_orientSamples, 'g'),
        unchangingStreak: longestStreak(_orientSamples)
      } : null
    };
  }


  // ============================================
  // HANDLERY ZDARZEŃ
  // ============================================

  function _onFocusIn(e) {
    if (!_isFormField(e.target)) return;
    var name = _getFieldName(e.target);
    _focusedField = name;
    _fieldFocusTime[name] = _now();
    _fieldFocusCount[name] = (_fieldFocusCount[name] || 0) + 1;
    _lastActivityTime = _now();

    // track field order (unique)
    if (!_fieldOrder.length || _fieldOrder[_fieldOrder.length - 1].field !== name) {
      _fieldOrder.push({ field: name, ts: _now() });
    }

    // setup autofill detection - tylko dla pól tekstowych
    // (checkbox/radio/select nie generują keystrokes z definicji)
    if (_isTextInput(e.target) && !_autofillSuspects[name]) {
      _autofillSuspects[name] = { hadKeystrokes: false, hadInput: false };
    }
  }

  function _onFocusOut(e) {
    if (!_isFormField(e.target)) return;
    var name = _getFieldName(e.target);

    if (_fieldFocusTime[name]) {
      var duration = _now() - _fieldFocusTime[name];
      _fieldDurations[name] = (_fieldDurations[name] || 0) + duration;

      // detekcja rapid fill (< 300ms z wartością)
      // - tylko dla pól tekstowych (select/checkbox z natury są natychmiastowe)
      // - dedup per pole: jedno pole = jeden rapid_fill (nie spam przy każdym blur)
      if (_isTextInput(e.target)
          && duration < 300
          && e.target.value
          && e.target.value.length > 3
          && !_rapidFields.some(function (rf) { return rf.field === name; })) {
        _rapidFields.push({
          field: name,
          duration: duration,
          length: e.target.value.length,
          time: _now()
        });
      }
    }

    // field length at blur
    if (e.target.value) {
      _fieldLengthsAtBlur[name] = e.target.value.length;
    }

    // autofill detection: input changed but no keystrokes in this field
    // suspect istnieje tylko dla _isTextInput (z _onFocusIn)
    var suspect = _autofillSuspects[name];
    if (suspect && suspect.hadInput && !suspect.hadKeystrokes && e.target.value && e.target.value.length > 2) {
      _log('autofill_detected', name, 'length', e.target.value.length);
      suspect.autofilled = true;
    }

    _focusedField = null;
    _lastActivityTime = _now();
  }

  function _onKeyDown(e) {
    _totalKeystrokes++;
    _lastActivityTime = _now();
    _trackTrust(e, 'keydown');

    var target = e.target;
    if (!_isFormField(target)) return;
    var name = _getFieldName(target);

    // mark field as having keystrokes (for autofill detection)
    if (_autofillSuspects[name]) {
      _autofillSuspects[name].hadKeystrokes = true;
    }

    // keystroke timing
    if (!_fieldKeystrokeTimes[name]) _fieldKeystrokeTimes[name] = [];
    _fieldKeystrokeTimes[name].push(_now());

    // keystroke dynamics: pairing keydown→keyup (dwell) + keyup→keydown (flight)
    var kcode = e.code || e.key || 'k';
    if (!_keyDownAt[name]) _keyDownAt[name] = {};
    if (_keyDownAt[name][kcode] === undefined) _keyDownAt[name][kcode] = _now();
    if (_lastKeyUpAt[name] !== undefined) {
      var flight = _now() - _lastKeyUpAt[name];
      if (flight >= 0 && flight < 5000) {
        if (!_fieldFlight[name]) _fieldFlight[name] = [];
        _fieldFlight[name].push(flight);
      }
    }
    if (!_isLoginField(name)) _recordSeq('kd', name, e.isTrusted);

    // backspace tracking
    // _backspaceCount: globalny licznik (też dla _login_*) — używany do rytmu
    // _correctedFields: per-pole, pomijamy _login_* (filtrowane i tak później)
    if (e.key === 'Backspace' || e.key === 'Delete') {
      _backspaceCount++;
      if (!_isLoginField(name)) {
        _correctedFields[name] = (_correctedFields[name] || 0) + 1;
      }
    }

    // Tab key - szybkie Tab+Tab+Tab = autofill navigation
    if (e.key === 'Tab') {
      if (!_onKeyDown._lastTabTime) _onKeyDown._lastTabTime = 0;
      var tabGap = _now() - _onKeyDown._lastTabTime;
      _onKeyDown._lastTabTime = _now();
      if (tabGap < 200) {
        _onKeyDown._rapidTabCount = (_onKeyDown._rapidTabCount || 0) + 1;
        if (_onKeyDown._rapidTabCount > 5) {
          _log('rapid_tab_navigation', _onKeyDown._rapidTabCount);
        }
      } else {
        _onKeyDown._rapidTabCount = 0;
      }
    }
  }
  _onKeyDown._lastTabTime = 0;
  _onKeyDown._rapidTabCount = 0;

  function _onKeyUp(e) {
    _trackTrust(e, 'keyup');
    var target = e.target;
    if (!_isFormField(target)) return;
    var name = _getFieldName(target);
    var kcode = e.code || e.key || 'k';

    // dwell: czas trzymania tego samego klawisza (keydown→keyup)
    if (_keyDownAt[name] && _keyDownAt[name][kcode] !== undefined) {
      var dwell = _now() - _keyDownAt[name][kcode];
      delete _keyDownAt[name][kcode];
      if (dwell >= 0 && dwell < 2000) {
        if (!_fieldDwell[name]) _fieldDwell[name] = [];
        _fieldDwell[name].push(dwell);
      }
    }
    _lastKeyUpAt[name] = _now();
  }

  function _onInput(e) {
    var target = e.target;
    if (!_isFormField(target)) return;
    var name = _getFieldName(target);
    _lastActivityTime = _now();

    _fieldChangeCount[name] = (_fieldChangeCount[name] || 0) + 1;

    // mark field as having input for autofill detection
    if (_autofillSuspects[name]) {
      _autofillSuspects[name].hadInput = true;
    }

    // track select/dropdown changes separately
    if (target.tagName.toUpperCase() === 'SELECT') {
      if (!_selectChanges[name]) _selectChanges[name] = [];
      _selectChanges[name].push({ value: target.value, ts: _now() });
    }

    // historia wartości
    if (!_fieldValues[name]) _fieldValues[name] = [];
    _fieldValues[name].push({
      value: String(target.value).substring(0, 4) + '***',  // zaciemnione dla prywatności
      len: (target.value || '').length,
      ts: _now()
    });
  }

  function _onPaste(e) {
    _totalPastes++;
    _lastActivityTime = _now();
    _trackTrust(e, 'paste');

    if (!_firstPasteTime) _firstPasteTime = _now();
    _lastPasteTime = _now();

    var target = e.target;
    if (!_isFormField(target)) return;
    var name = _getFieldName(target);

    _fieldPasteCount[name] = (_fieldPasteCount[name] || 0) + 1;
    if (!_isLoginField(name)) _recordSeq('paste', name, e.isTrusted);

    // pasted content length (from clipboardData, not the value itself for privacy)
    var pastedLen = 0;
    try {
      var clipData = (e.clipboardData || window.clipboardData);
      if (clipData) {
        pastedLen = (clipData.getData('text') || '').length;
      }
    } catch (ex) { }

    _log('paste_detected', name, 'length', pastedLen);
  }

  function _onCopy(e) {
    _totalCopies++;
    _lastActivityTime = _now();

    var target = e.target;
    if (_isFormField(target)) {
      _log('copy_from_field', _getFieldName(target));
    }
  }

  function _onClick(e) {
    _totalClicks++;
    _lastActivityTime = _now();
    _trackTrust(e, 'click');
    _recordSeq('click', _isFormField(e.target) ? _getFieldName(e.target) : null, e.isTrusted);
  }

  function _onDeviceMotion(e) {
    _motionEventCount++;
    if (_motionSamples.length >= _SENSOR_MAX) return;
    var a = e.accelerationIncludingGravity;
    if (a && (a.x !== null || a.y !== null || a.z !== null)) {
      _motionSamples.push({ x: a.x || 0, y: a.y || 0, z: a.z || 0 });
    }
  }

  function _onDeviceOrientation(e) {
    _orientEventCount++;
    if (_orientSamples.length >= _SENSOR_MAX) return;
    if (e.alpha !== null || e.beta !== null || e.gamma !== null) {
      _orientSamples.push({ a: e.alpha || 0, b: e.beta || 0, g: e.gamma || 0 });
    }
  }

  function _onMouseMove(e) {
    // throttle to ~50ms to reduce CPU (skip if less than 50ms since last)
    var now = _now();
    if (now - _mouseMoveThrottle < 50) return;
    _mouseMoveThrottle = now;

    if (_lastMousePos) {
      var dx = e.clientX - _lastMousePos.x;
      var dy = e.clientY - _lastMousePos.y;
      var dist = Math.sqrt(dx * dx + dy * dy);
      _mouseDistance += dist;

      // collect segments for linearity analysis (keep last 20)
      if (dist > 5) {
        _mouseSegments.push({ dx: dx, dy: dy, dist: dist });
        if (_mouseSegments.length > 100) {
          _mouseSegments = _mouseSegments.slice(-50);
        }
      }
    }
    _lastMousePos = { x: e.clientX, y: e.clientY };
    _lastActivityTime = now;
  }

  function _onContextMenu(e) {
    _rightClickCount++;
    _lastActivityTime = _now();

    if (_isFormField(e.target)) {
      var name = _getFieldName(e.target);
      _rightClickFields.push({ field: name, ts: _now() });
      _log('right_click_on_field', name);
    }
  }

  // Analyze mouse linearity - boty poruszają mysz w prostych liniach
  function _computeMouseLinearity() {
    if (_mouseSegments.length < 10) return null;

    var straightCount = 0;
    var totalChecked = 0;

    // sprawdź consecutive segmenty - czy kąt między nimi jest bliski 0
    for (var i = 2; i < _mouseSegments.length; i++) {
      var s1 = _mouseSegments[i - 1];
      var s2 = _mouseSegments[i];

      // cross product - blisko 0 = proste linie
      var cross = Math.abs(s1.dx * s2.dy - s1.dy * s2.dx);
      var normalization = s1.dist * s2.dist;

      if (normalization > 0) {
        totalChecked++;
        if (cross / normalization < 0.1) {
          straightCount++;
        }
      }
    }

    if (totalChecked === 0) return null;

    var linearityRatio = straightCount / totalChecked;

    return {
      ratio: Math.round(linearityRatio * 100) / 100,
      segmentsAnalyzed: totalChecked,
      // ratio > 0.7 = podejrzanie proste linie myszy (bot/skrypt)
      suspiciouslyLinear: linearityRatio > 0.7 && totalChecked > 15
    };
  }

  function _onTouchStart() {
    _touchCount++;
    _isTouchDevice = true;
    _lastActivityTime = _now();
  }

  function _onScroll() {
    _scrollCount++;
    _lastActivityTime = _now();
  }

  function _onVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      _tabSwitchCount++;
      _tabHiddenSince = _now();

      // sprawdź co user robił przed przełączeniem
      var ctx = { field: _focusedField || null, ts: _now(), step: _currentStep };
      _tabSwitchContext.push(ctx);

      if (_focusedField) {
        _log('tab_switch_during_field', _focusedField, 'step', _currentStep);
      }
    } else {
      if (_tabHiddenSince) {
        var away = _now() - _tabHiddenSince;
        _tabAwayDuration += away;

        // update context with away duration
        if (_tabSwitchContext.length > 0) {
          _tabSwitchContext[_tabSwitchContext.length - 1].awayDuration = away;
        }

        _tabHiddenSince = null;

        _log('tab_return', 'away_ms', away);
      }
    }
  }

  // Keyboard resize tracking: na mobile klawiatura wirtualna generuje resize
  // przy każdym focus/blur inputa — to nie są "prawdziwe" resize
  var _keyboardResizeCount = 0;

  function _onResize() {
    _windowResizeCount++;

    // Jeśli pole jest aktywne na touch device — to prawdopodobnie klawiatura
    if (_isTouchDevice && _focusedField) {
      _keyboardResizeCount++;
    }

    _checkDevTools();
  }

  // DevTools detection - wielowarstwowa
  // Sprawdzana: przy resize, co 3s (idle checker), i raz przy init via Worker
  var _devToolsCheckCount = 0;
  var _devToolsMaxChecks = 60;

  // iOS Safari nie pozwala otwierać DevTools z urządzenia (tylko via macOS Safari Web Inspector
  // przez kabel). Wszystkie wcześniejsze "DT detected" na iPhone/iPad to fałszywe pozytywy.
  function _isIOS() {
    var ua = navigator.userAgent || '';
    return (/iPhone|iPad|iPod/.test(ua) && !window.MSStream)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPad iOS 13+
  }

  // In-app browsery (Messenger/FB/IG/Line/WebView) i PWA standalone często mają outerHeight==0
  // lub niestabilne wymiary — nie używamy tam zero_outer ani console tricks.
  function _isHostedBrowserContext() {
    var ua = navigator.userAgent || '';
    if (/FBAN|FBAV|Instagram|Messenger|Line\/|MicroMessenger|TikTok|; wv\)/i.test(ua)) return true;
    try {
      if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
    } catch (e) { }
    if (window.navigator && window.navigator.standalone === true) return true;
    return false;
  }

  function _checkDevTools() {
    if (_devToolsOpened) return;
    _devToolsCheckCount++;
    if (_devToolsCheckCount > _devToolsMaxChecks) return;

    // iOS Safari — pomiń wszystkie metody size_diff/zero_outer/console tricks
    // (DT niedostępne lokalnie, zostają tylko worker_debugger i worker_timeout)
    var iOS = _isIOS();
    var hosted = _isHostedBrowserContext();

    var detected = false;
    var method = '';

    // 1) outerWidth/innerWidth difference (DevTools docked)
    // Na mobile (iPhone/Android) heightDiff 200-300px to normalne (pasek Safari/Chrome)
    // Podnosimy próg dla touch devices żeby uniknąć false positive
    // Skip jeśli outer* są zero — wartości niestabilne, diff bezwartościowy
    // Sanity cap: diff > 800 to display scaling / multi-monitor / fullscreen, nie DevTools
    if (!iOS) {
      var widthDiff = Math.abs(window.outerWidth - window.innerWidth);
      var heightDiff = Math.abs(window.outerHeight - window.innerHeight);
      var outerStable = window.outerWidth > 0 && window.outerHeight > 0;
      var isMobileUA = /Mobi|Android|armv/i.test(navigator.userAgent) || (navigator.maxTouchPoints > 2);
      var sizeThreshold = (_isTouchDevice || isMobileUA) ? 350 : 160;
      var SIZE_SANITY_CAP = 800;
      if (outerStable
          && widthDiff < SIZE_SANITY_CAP
          && heightDiff < SIZE_SANITY_CAP
          && (widthDiff > sizeThreshold || (!(_isTouchDevice || isMobileUA) && heightDiff > 160))) {
        detected = true;
        method = 'size_diff_w' + widthDiff + '_h' + heightDiff;
      }
    }

    // UWAGA: usunięte metody console.dir i console.log('%c', re).
    // Powód: wywoływane co 2s przez _checkIdle generowały widoczny spam w konsoli
    // (plynność + stealth). Do tego console_tostring dał 119 false positives w logach.
    // Zostaje: size_diff, zero_outer, worker_debugger — wszystkie ciche.

    // 4) window.outerHeight === 0 (headless) — pomiń w hosted browser / PWA standalone
    if (!detected && !hosted && (window.outerHeight === 0 || window.outerWidth === 0)) {
      detected = true;
      method = 'zero_outer';
    }

    if (detected) {
      _devToolsOpened = true;
      _log('devtools_detected', method);
    }
  }

  // Jednorazowa detekcja via Web Worker z debugger statement
  // Jedyna metoda która wiarygodnie wykrywa DevTools w OSOBNYM OKNIE
  // Worker nie blokuje main thread - debugger pauzuje tylko Worker
  function _checkDevToolsViaWorker() {
    if (_devToolsOpened) return;
    try {
      var workerCode =
        'self.onmessage=function(){' +
          'var t=Date.now();' +
          'debugger;' +           // przechwycony przez DevTools nawet w osobnym oknie
          'self.postMessage(Date.now()-t);' +
        '};';
      var blob = new Blob([workerCode], { type: 'application/javascript' });
      var url = URL.createObjectURL(blob);
      var w = new Worker(url);

      // timeout: jeśli worker nie odpowie w 3s = debugger go pauzuje = DevTools otwarte
      var timer = setTimeout(function () {
        if (!_devToolsOpened) {
          _devToolsOpened = true;
          _log('devtools_detected', 'worker_timeout');
        }
        try { w.terminate(); } catch (e) { }
        URL.revokeObjectURL(url);
      }, 3000);

      w.onmessage = function (e) {
        clearTimeout(timer);
        try { w.terminate(); } catch (ex) { }
        URL.revokeObjectURL(url);
        // bez DevTools: <5ms, z DevTools: worker był spauzowany
        if (e.data > 100 && !_devToolsOpened) {
          _devToolsOpened = true;
          _log('devtools_detected', 'worker_debugger_' + e.data + 'ms');
        }
      };

      w.onerror = function () {
        clearTimeout(timer);
        try { w.terminate(); } catch (ex) { }
        URL.revokeObjectURL(url);
      };

      w.postMessage('check');
    } catch (e) {
      // Worker niedostępny (IE, lub blob URL zablokowany) - skip
    }
  }

  // Honeypot: wstrzyknięcie pola-przynęty do DOM. Poza <form> (Vue re-rendery go
  // nie kasują); boty wypełniające inputy na stronie i tak je znajdują.
  function _createHoneypot() {
    if (_honeypotEl || !document.body) return;
    try {
      var wrap = document.createElement('div');
      // offscreen zamiast display:none — część botów pomija display:none,
      // a inne sprawdzają tylko visibility/hidden attribute
      wrap.style.cssText = 'position:absolute;left:-9999px;top:-9999px;height:0;width:0;overflow:hidden;';
      wrap.setAttribute('aria-hidden', 'true');

      var input = document.createElement('input');
      input.type = 'text';
      input.name = _HONEYPOT_NAME;
      input.id = _HONEYPOT_NAME;
      input.autocomplete = 'off';
      input.tabIndex = -1;

      wrap.appendChild(input);
      document.body.appendChild(wrap);
      _honeypotWrap = wrap;
      _honeypotEl = input;

      input.addEventListener('focus', function () {
        _honeypotMeta.focused++;
      }, true);
      input.addEventListener('input', function () {
        _honeypotMeta.inputs++;
        _honeypotMeta.len = (input.value || '').length;
        if (!_honeypotFilled && _honeypotMeta.len > 0) {
          _honeypotFilled = true;
          _honeypotMeta.firstTs = _now() - _formStartTime;
          _log('honeypot_filled', 'len', _honeypotMeta.len);
        }
      }, true);
    } catch (e) { }
  }

  // Bot może ustawić el.value bezpośrednio (bez eventu 'input') — łapiemy to
  // pollingiem (idle checker) i przy każdym snapshocie.
  function _checkHoneypotValue() {
    if (_honeypotEl && !_honeypotFilled && _honeypotEl.value) {
      _honeypotFilled = true;
      _honeypotMeta.len = _honeypotEl.value.length;
      _honeypotMeta.firstTs = _now() - _formStartTime;
      _log('honeypot_filled', 'len', _honeypotMeta.len, 'via', 'value_poll');
    }
  }

  // idle detection - co 2s sprawdzamy
  var _idleChecker = null;

  function _checkIdle() {
    if (!_lastActivityTime) return;

    // DevTools check co 2s
    _checkDevTools();
    _checkHoneypotValue();

    var idle = _now() - _lastActivityTime;

    // hesitation w polu
    if (_focusedField && idle > _HESITATION_THRESHOLD && idle < _IDLE_THRESHOLD) {
      var last = _hesitations.length > 0 ? _hesitations[_hesitations.length - 1] : null;
      if (!last || last.field !== _focusedField || (_now() - last.ts > 10000)) {
        _hesitations.push({
          field: _focusedField,
          duration: idle,
          ts: _now()
        });
      }
    }

    // pełna bezczynność
    if (idle > _IDLE_THRESHOLD && !_idleStart) {
      _idleStart = _lastActivityTime;
    }
    if (idle < _IDLE_THRESHOLD && _idleStart) {
      _idlePeriods.push({
        start: _idleStart,
        duration: _now() - _idleStart
      });
      _idleStart = null;
    }
  }


  // ============================================
  // PUBLICZNE API
  // ============================================

  return {

    /**
     * Inicjalizacja trackera - wywoływane raz przy starcie aplikacji
     */
    init: function () {
      if (_initialized) return this;

      _formStartTime = _now();
      _stepStartTime = _now();
      _lastActivityTime = _now();

      // form field events
      document.addEventListener('focusin', _onFocusIn, true);
      document.addEventListener('focusout', _onFocusOut, true);
      document.addEventListener('keydown', _onKeyDown, true);
      document.addEventListener('keyup', _onKeyUp, true);
      document.addEventListener('input', _onInput, true);
      document.addEventListener('paste', _onPaste, true);
      document.addEventListener('copy', _onCopy, true);

      // interaction events
      document.addEventListener('click', _onClick, true);
      document.addEventListener('contextmenu', _onContextMenu, true);
      document.addEventListener('mousemove', _onMouseMove, { capture: true, passive: true });
      document.addEventListener('touchstart', _onTouchStart, { capture: true, passive: true });
      document.addEventListener('scroll', _onScroll, { capture: true, passive: true });

      // visibility
      document.addEventListener('visibilitychange', _onVisibilityChange, true);

      // resize / devtools
      window.addEventListener('resize', _onResize, true);

      // sensor mobilny — emulator detection (ciche nasłuchiwanie; iOS 13+ wymaga
      // permission via gest, więc tam po prostu nic nie zbierzemy — i nie flagujemy)
      if (window.DeviceMotionEvent) {
        window.addEventListener('devicemotion', _onDeviceMotion, { passive: true });
      }
      if (window.DeviceOrientationEvent) {
        window.addEventListener('deviceorientation', _onDeviceOrientation, { passive: true });
      }

      // idle checker co 5s — wcześniej 2s, ale DT check już nie robi console-hacków
      // więc może chodzić rzadziej bez utraty precyzji (hesitation threshold = 5s)
      _idleChecker = setInterval(_checkIdle, 5000);

      // log environment na start — backend odczytuje je z tej linii bt_init
      var env = _detectEnvironment();
      _log('bt_init', 'environment', env);

      // ostrzeżenia od razu
      if (env.webdriver) _log('WARNING_webdriver_detected');
      if (env.headless) _log('WARNING_headless_detected');
      if (env.phantomJS) _log('WARNING_phantomjs_detected');
      if (env.selenium) _log('WARNING_selenium_detected');

      // sprawdź DevTools od razu (mogą być otwarte przed załadowaniem strony)
      setTimeout(_checkDevTools, 500);
      // worker-based check - wykrywa DevTools w osobnym oknie
      setTimeout(_checkDevToolsViaWorker, 1000);

      // honeypot — body może jeszcze nie istnieć przy bardzo wczesnym init
      if (document.body) {
        _createHoneypot();
      } else {
        document.addEventListener('DOMContentLoaded', _createHoneypot);
      }

      _initialized = true;
      return this;
    },


    /**
     * Oznacza że user jest zalogowany (known identity)
     * Wpływa na interpretację risk signals
     * @param {boolean} authenticated
     */
    setAuthenticated: function (authenticated) {
      _isAuthenticated = !!authenticated;
      _log('auth_state', authenticated ? 'logged_in' : 'logged_out');
    },


    /**
     * Powiadamia BT że formularz został wypełniony programowo (fillForm / OTP prefill)
     * Dzięki temu BT wie że brak keystroke'ów to normalne a nie podejrzane
     * @param {Array} fieldNames - opcjonalnie lista prefillowanych pól
     */
    onFormPrefilled: function (fieldNames) {
      _formPrefilled = true;
      _prefillTime = _now();
      _prefilledFields = fieldNames || [];
      _log('form_prefilled', 'fields', _prefilledFields.length);
    },


    /**
     * Oznacza fazę procesu wniosku
     * @param {string} phase - 'new' | 'continuation' | 'resubmit_after_verification'
     * @param {boolean} hasLoanId - czy formularz ma już loan_id
     */
    setApplicationPhase: function (phase, hasLoanId) {
      _applicationPhase = phase || 'new';
      _hasLoanId = !!hasLoanId;
      _log('app_phase', phase, 'loanId', _hasLoanId);
    },


    /**
     * Powiadomienie o zmianie kroku formularza
     * @param {number} newStep - numer nowego kroku
     * @param {number} oldStep - numer poprzedniego kroku
     */
    onStepChange: function (newStep, oldStep) {
      var now = _now();

      // zamknij poprzedni krok
      if (_stepTimeline.length > 0) {
        var last = _stepTimeline[_stepTimeline.length - 1];
        last.leaveTime = now;
        last.duration = now - last.enterTime;
      }

      // cofanie?
      if (oldStep !== undefined && newStep < oldStep) {
        _backStepCount++;
        _log('step_back', 'from', oldStep, 'to', newStep);
      }

      // nowy krok
      _stepTimeline.push({
        step: newStep,
        enterTime: now,
        leaveTime: null,
        duration: null
      });

      _currentStep = newStep;
      _stepStartTime = now;

      _log('step_change', 'step', newStep, 'totalTime', now - _formStartTime);
    },


    /**
     * Powiadomienie o zmianie wartości kalkulatora (slider kwoty)
     * @param {number} value - nowa kwota
     */
    onCalcChange: function (value) {
      _calcChangeCount++;
      var now = _now();

      // Burst detection: przesunięcie slidera generuje dziesiątki eventów w <1s
      // Grupujemy zmiany w <300ms odstępie jako jeden "gest"
      var lastVal = _calcValues.length > 0 ? _calcValues[_calcValues.length - 1] : null;
      if (lastVal && (now - lastVal.ts) < 300) {
        // update ostatni wpis zamiast dodawać nowy (to ten sam gest)
        lastVal.value = value;
        lastVal.ts = now;
        lastVal.burstCount = (lastVal.burstCount || 1) + 1;
      } else {
        _calcValues.push({ value: value, ts: now, burstCount: 1 });
      }

      // one-shot log — wcześniej fire'ował co każdy event > 10, generował 25k+ spam linii dziennie.
      // Realny calcChangeCount jest zawsze w snapshocie (calculator.changeCount).
      if (_calcChangeCount > 10 && !_calcExcessiveLogged) {
        _calcExcessiveLogged = true;
        _log('excessive_calc_changes', _calcChangeCount);
      }
    },


    /**
     * Powiadomienie o próbie wysłania formularza
     * @param {string} endpoint - endpoint na który wysyłany jest formularz
     * @param {boolean} valid - czy formularz był valid
     */
    onSubmitAttempt: function (endpoint, valid) {
      _submitAttempts++;

      _log('submit_attempt', 'endpoint', endpoint, 'valid', valid, 'attempt', _submitAttempts);
    },


    /**
     * Powiadomienie o błędzie walidacji
     * @param {Array} fields - lista pól z błędem
     */
    onValidationError: function (fields) {
      _validationErrors.push({
        fields: fields,
        step: _currentStep,
        ts: _now()
      });

      _log('validation_error', 'fields', fields, 'step', _currentStep);
    },


    /**
     * Generuje pełny snapshot behawioralny - do wysłania przy submicie
     * @returns {Object} dane behawioralne
     */
    getSnapshot: function () {
      var now = _now();
      var totalDuration = now - _formStartTime;

      // zamknij aktualny krok
      if (_stepTimeline.length > 0) {
        var lastStep = _stepTimeline[_stepTimeline.length - 1];
        if (!lastStep.leaveTime) {
          lastStep.leaveTime = now;
          lastStep.duration = now - lastStep.enterTime;
        }
      }

      // keystroke rhythms per pole (pomijaj pola logowania)
      var rhythms = {};
      for (var f in _fieldKeystrokeTimes) {
        if (_fieldKeystrokeTimes.hasOwnProperty(f) && !/^_login_/.test(f)) {
          rhythms[f] = _computeKeystrokeRhythm(f);
        }
      }

      // podsumowanie pól z podejrzanymi sygnałami (pomijaj pola logowania)
      // Paste w polach z _LEGIT_PASTE_RX (IBAN, email_confirm, kod SMS) nie jest podejrzane —
      // to oczekiwany flow użytkownika (kopiuje z dokumentu/maila/SMS).
      var suspiciousFields = [];
      for (var sf in _fieldPasteCount) {
        if (_fieldPasteCount[sf] > 0 && !_isLoginField(sf) && !_isLegitPasteField(sf)) {
          suspiciousFields.push({ field: sf, reason: 'paste', count: _fieldPasteCount[sf] });
        }
      }
      for (var rf = 0; rf < _rapidFields.length; rf++) {
        if (_isLoginField(_rapidFields[rf].field)) continue;
        suspiciousFields.push({ field: _rapidFields[rf].field, reason: 'rapid_fill', duration: _rapidFields[rf].duration });
      }
      for (var cf in _correctedFields) {
        if (_isLoginField(cf)) continue;
        // Próg per-pole: pola maskowane (v-mask) generują syntetyczne backspace przy reformacie,
        // więc próg 5 dla nich = false positive (np. client_name miał p50=10 backspace na 6 znakach).
        var corrThreshold = _isMaskField(cf) ? 20 : 10;
        if (_correctedFields[cf] > corrThreshold) {
          suspiciousFields.push({ field: cf, reason: 'excessive_corrections', count: _correctedFields[cf] });
        }
      }
      // autofilled fields — _autofillSuspects istnieje tylko dla _isTextInput (od _onFocusIn)
      for (var af in _autofillSuspects) {
        if (_autofillSuspects[af].autofilled && !_isLoginField(af)) {
          suspiciousFields.push({ field: af, reason: 'autofill_no_keystrokes' });
        }
      }

      // policz pastes/right-clicks/tab-switches z pominięciem legit kontekstów
      var nonLegitPastes = 0;
      for (var pf in _fieldPasteCount) {
        if (!_isLegitPasteField(pf) && !_isLoginField(pf)) {
          nonLegitPastes += _fieldPasteCount[pf];
        }
      }
      var nonLegitRightClicks = 0;
      for (var rci = 0; rci < _rightClickFields.length; rci++) {
        var rcfield = _rightClickFields[rci].field;
        if (!_isLegitPasteField(rcfield) && !_isLoginField(rcfield)) {
          nonLegitRightClicks++;
        }
      }
      var nonLegitTabSwitches = 0;
      for (var tsi = 0; tsi < _tabSwitchContext.length; tsi++) {
        if (!_isLegitTabField(_tabSwitchContext[tsi].field)) {
          nonLegitTabSwitches++;
        }
      }

      // mouse linearity — pomijamy obliczenie na typowych touch sesjach (zawsze ratio≈0)
      var mouseLinearity = (_isTouchDevice && _touchCount > 5) ? null : _computeMouseLinearity();

      var snapshot = {

        // === TIMING ===
        timing: {
          totalDuration: totalDuration,
          totalDurationSec: Math.round(totalDuration / 1000),
          fieldDurations: _fieldDurations,
          stepTimeline: _stepTimeline,
          idlePeriods: _idlePeriods,
          hesitations: _hesitations
        },

        // === INTERAKCJE ===
        interactions: {
          totalKeystrokes: _totalKeystrokes,
          totalClicks: _totalClicks,
          totalPastes: _totalPastes,
          totalCopies: _totalCopies,
          totalScrolls: _scrollCount,
          totalTouches: _touchCount,
          mouseDistance: Math.round(_mouseDistance),
          mouseLinearity: mouseLinearity,
          backspaceCount: _backspaceCount,
          rightClicks: _rightClickCount,
          isTouchDevice: _isTouchDevice,
          trustedEvents: _trustedEvents,
          untrustedEvents: _untrustedEvents,
          untrustedByType: _untrustedByType
        },

        // === PER-FIELD DETAIL ===
        fields: {
          focusCount: _fieldFocusCount,
          changeCount: _fieldChangeCount,
          pasteCount: _fieldPasteCount,
          correctedFields: _correctedFields,
          keystrokeRhythm: rhythms,
          rapidFills: _rapidFields,
          fieldLengths: _fieldLengthsAtBlur,
          fieldOrder: _fieldOrder,
          autofillSuspects: (function () {
            var result = {};
            for (var k in _autofillSuspects) {
              if (_autofillSuspects[k].autofilled) result[k] = true;
            }
            return result;
          })(),
          selectChanges: _selectChanges,
          // keystroke dynamics per pole (dwell + flight) — format jak Nethone
          keystrokeDynamics: (function () {
            var kd = {};
            for (var f in _fieldDwell) {
              if (_fieldDwell.hasOwnProperty(f) && !/^_login_/.test(f)) {
                kd[f] = kd[f] || {};
                kd[f].dwell = _stats(_fieldDwell[f]);
              }
            }
            for (var ff in _fieldFlight) {
              if (_fieldFlight.hasOwnProperty(ff) && !/^_login_/.test(ff)) {
                kd[ff] = kd[ff] || {};
                kd[ff].flight = _stats(_fieldFlight[ff]);
              }
            }
            return kd;
          })()
        },

        // === SENSOR MOBILNY (emulator detection) ===
        sensor: _analyzeSensor(),

        // === SUROWA SEKWENCJA ZDARZEŃ (tamper-detection na backendzie) ===
        rawSequence: {
          events: _eventSeq.slice(),
          total: _eventSeqCounter,        // pełna liczba (buffer mógł obciąć)
          truncated: _eventSeqCounter > _eventSeq.length
        },

        // === NAWIGACJA ===
        navigation: {
          stepsVisited: _stepTimeline.length,
          backSteps: _backStepCount,
          tabSwitches: _tabSwitchCount,
          tabAwayDuration: _tabAwayDuration,
          tabAwayDurationSec: Math.round(_tabAwayDuration / 1000),
          tabSwitchContext: _tabSwitchContext,
          windowResizes: _windowResizeCount,
          keyboardResizes: _keyboardResizeCount
        },

        // === KALKULATOR ===
        calculator: {
          changeCount: _calcChangeCount,
          gestureCount: _calcValues.length,  // ile realnych gestów (po debounce burst)
          values: _calcValues
        },

        // === RISK SIGNALS ===
        riskSignals: {
          suspiciousFields: suspiciousFields,
          devToolsOpened: _devToolsOpened,
          submitAttempts: _submitAttempts,
          validationErrors: _validationErrors,
          formFilledTooFast: totalDuration < 30000 && !_formPrefilled,    // nie flaguj przy prefill
          formFilledVerySlow: totalDuration > 1800000,
          // tab-switch: pomijamy switche w kontekście logowania/SMS/email_confirm (legit flow)
          excessiveTabSwitching: nonLegitTabSwitches > 15,
          tabSwitchTotal: _tabSwitchCount,
          tabSwitchNonLegit: nonLegitTabSwitches,
          noMouseMovement: _mouseDistance < 100 && !_isTouchDevice,
          suspiciousMousePattern: mouseLinearity && mouseLinearity.suspiciouslyLinear,
          // noKeystrokes: nie flaguj jeśli user wkleił dane (legit flow copy-from-email)
          noKeystrokes: _totalKeystrokes === 0 && !_formPrefilled && _totalPastes < 2,
          // highPasteRatio liczy tylko paste w polach gdzie paste = podejrzany
          // (paste w client_bank_account / email_confirm / Kod SMS = oczekiwane zachowanie)
          highPasteRatio: nonLegitPastes > 5,
          rightClickOnFields: nonLegitRightClicks > 0,
          copyPastePattern: nonLegitTabSwitches >= 3 && nonLegitPastes > 2,
          // syntetyczne pisanie/wklejanie (isTrusted===false na keydown/paste) = automation.
          // Świadomie NIE liczymy syntetycznych click — frameworki UI (Vuetify) generują
          // legit syntetyczne kliknięcia label→input, co dawałoby false positives.
          syntheticTyping: (_untrustedByType['keydown'] || 0) > 0 || (_untrustedByType['paste'] || 0) > 0,
          // Android z dotykiem ale BEZ żadnych eventów sensora = emulator/spoof.
          // iOS pomijamy (wymaga permission via gest → brak danych to nie sygnał).
          mobileNoSensor: /Android/i.test(navigator.userAgent || '')
            && _isTouchDevice && _motionEventCount === 0 && _orientEventCount === 0,
          // honeypot: niewidzialne pole wypełnione = bot (człowiek go nie widzi)
          honeypotFilled: (function () {
            _checkHoneypotValue();
            return _honeypotFilled;
          })(),
          honeypotDetail: _honeypotFilled ? {
            len: _honeypotMeta.len,
            inputs: _honeypotMeta.inputs,
            focused: _honeypotMeta.focused,
            firstTs: _honeypotMeta.firstTs
          } : null,
          // sensor zwraca stałe wartości (długa seria identycznych próbek) = spoof
          sensorSpoofed: (function () {
            var s = _analyzeSensor();
            if (!s) return false;
            var m = s.motion && s.motion.unchangingStreak > 50;
            var o = s.orientation && s.orientation.unchangingStreak > 50;
            return !!(m || o);
          })(),
          fieldRefocusPattern: (function () {
            var multi = [];
            for (var fn in _fieldFocusCount) {
              if (_fieldFocusCount[fn] > 3) multi.push({ field: fn, count: _fieldFocusCount[fn] });
            }
            return multi;
          })()
        },

        // === AUTH & PREFILL CONTEXT ===
        context: {
          isAuthenticated: _isAuthenticated,
          formPrefilled: _formPrefilled,
          prefillTime: _prefillTime,
          prefilledFieldCount: _prefilledFields.length,
          applicationPhase: _applicationPhase,
          hasLoanId: _hasLoanId
        },

        // === META ===
        meta: {
          version: '1.6.0',
          collectedAt: new Date().toISOString(),
          currentStep: _currentStep
        }
      };

      return snapshot;
    },


    /**
     * Wysyła snapshot behawioralny przez LogService
     * Debounce: ten sam event w <500ms jest ignorowany
     * @param {string} event - nazwa zdarzenia (np. 'form_submit', 'step_complete')
     */
    sendSnapshot: function (event) {
      var now = _now();
      var key = event || 'manual';

      // debounce: identyczny event w <500ms = duplikat (np. podwójne next→existCheck)
      if (this._lastSnapshotEvent === key && (now - this._lastSnapshotTime) < 500) {
        return;
      }
      this._lastSnapshotEvent = key;
      this._lastSnapshotTime = now;

      var snapshot = this.getSnapshot();
      _log('bt_snapshot', key, snapshot);
    },
    _lastSnapshotEvent: null,
    _lastSnapshotTime: 0,


    /**
     * Resetuje stan (np. po wylogowaniu)
     */
    reset: function () {
      _formStartTime = _now();
      _stepStartTime = _now();
      _currentStep = 1;
      _fieldFocusTime = {};
      _fieldDurations = {};
      _fieldKeystrokeTimes = {};
      _fieldChangeCount = {};
      _fieldPasteCount = {};
      _fieldValues = {};
      _fieldFocusCount = {};
      _totalKeystrokes = 0;
      _totalClicks = 0;
      _totalPastes = 0;
      _totalCopies = 0;
      _tabSwitchCount = 0;
      _tabAwayDuration = 0;
      _tabHiddenSince = null;
      _mouseDistance = 0;
      _lastMousePos = null;
      _scrollCount = 0;
      _hesitations = [];
      _rapidFields = [];
      _correctedFields = {};
      _backspaceCount = 0;
      _stepTimeline = [];
      _backStepCount = 0;
      _calcChangeCount = 0;
      _calcValues = [];
      _calcExcessiveLogged = false;
      _touchCount = 0;
      // NIE resetujemy _devToolsOpened - raz wykryte = wykryte do końca sesji
      // NIE resetujemy _honeypotFilled - bot raz złapany = złapany do końca sesji
      _windowResizeCount = 0;
      _keyboardResizeCount = 0;
      _submitAttempts = 0;
      _validationErrors = [];
      _focusedField = null;
      _lastActivityTime = _now();
      _idlePeriods = [];
      _idleStart = null;
      _fieldOrder = [];
      _autofillSuspects = {};

      // re-run worker check po resecie (DevTools mogły być otwarte po init)
      _devToolsCheckCount = 0;
      setTimeout(_checkDevTools, 100);
      setTimeout(_checkDevToolsViaWorker, 500);
      _rightClickCount = 0;
      _rightClickFields = [];
      _mouseSegments = [];
      _mouseMoveThrottle = 0;
      _fieldLengthsAtBlur = {};
      _tabSwitchContext = [];
      _firstPasteTime = null;
      _lastPasteTime = null;
      _selectChanges = {};
      // v1.5.0
      _untrustedEvents = 0;
      _trustedEvents = 0;
      _untrustedByType = {};
      _keyDownAt = {};
      _fieldDwell = {};
      _fieldFlight = {};
      _lastKeyUpAt = {};
      _motionSamples = [];
      _orientSamples = [];
      _motionEventCount = 0;
      _orientEventCount = 0;
      _eventSeq = [];
      _eventSeqCounter = 0;
      _isAuthenticated = false;
      _formPrefilled = false;
      _prefillTime = null;
      _prefilledFields = [];
      // NIE resetujemy _applicationPhase i _hasLoanId — faza procesu jest ciągła
    },


    /**
     * Cleanup - odrejestrowanie listenerów
     */
    destroy: function () {
      document.removeEventListener('focusin', _onFocusIn, true);
      document.removeEventListener('focusout', _onFocusOut, true);
      document.removeEventListener('keydown', _onKeyDown, true);
      document.removeEventListener('keyup', _onKeyUp, true);
      document.removeEventListener('input', _onInput, true);
      document.removeEventListener('paste', _onPaste, true);
      document.removeEventListener('copy', _onCopy, true);
      document.removeEventListener('click', _onClick, true);
      document.removeEventListener('contextmenu', _onContextMenu, true);
      document.removeEventListener('mousemove', _onMouseMove, true);
      document.removeEventListener('touchstart', _onTouchStart, true);
      document.removeEventListener('scroll', _onScroll, true);
      document.removeEventListener('visibilitychange', _onVisibilityChange, true);
      window.removeEventListener('resize', _onResize, true);
      window.removeEventListener('devicemotion', _onDeviceMotion);
      window.removeEventListener('deviceorientation', _onDeviceOrientation);

      if (_honeypotWrap && _honeypotWrap.parentNode) {
        _honeypotWrap.parentNode.removeChild(_honeypotWrap);
      }
      _honeypotWrap = null;
      _honeypotEl = null;

      if (_idleChecker) {
        clearInterval(_idleChecker);
        _idleChecker = null;
      }

      _initialized = false;
    }
  };

})();


if (typeof module !== 'undefined' && module.exports) {
  module.exports = BehaviorTracker;
}

export default BehaviorTracker;


// WEBPACK FOOTER //
// ./src/behaviorTracker.js